// --- START OF FILE SlotTokensDistribution.js ---

import React, { useMemo, useState } from 'react';
import { Target } from 'lucide-react';

const SlotTokensDistribution = ({ slot, sprints }) => {
  const [mode, setMode] = useState("tokens");
  const MIN_TEXT_WIDTH_PERCENT = 10; 

  const calculateSprintDataInSlot = (sprint, modeToCalc) => { // Renommé mode en modeToCalc pour éviter confusion
    const sprintStart = new Date(sprint.start_date);
    const sprintEnd   = new Date(sprint.end_date);
    const slotStart   = new Date(slot.start_date);
    const slotEnd     = new Date(slot.end_date);
  
    const totalSprintDays = (sprintEnd - sprintStart) / (1000 * 60 * 60 * 24);
    let ratio = 0;
    if (totalSprintDays > 0) {
      const intersectionStart = new Date(Math.max(sprintStart, slotStart));
      const intersectionEnd   = new Date(Math.min(sprintEnd, slotEnd));
      const intersectionDays = Math.max(((intersectionEnd - intersectionStart) / (1000 * 60 * 60 * 24)), 0);
      ratio = intersectionDays / totalSprintDays;
    }
  
    if (modeToCalc === "tokens") {
      return {
        total: Math.round((Number(sprint.total_tokens) || 0) * ratio),
        completed: Math.round((Number(sprint.tokens_completed) || 0) * ratio)
      };
    } else { // mode === "ca"
      return {
        total: Math.round((Number(sprint.valeur_a_produire) || 0) * ratio),
        completed: Math.round((Number(sprint.ca_produit) || 0) * ratio)
      };
    }
  };  

  const projectData = useMemo(() => {
    const sprintsWithSlotValues = sprints.map(sprint => {
      const tokensInSlot = calculateSprintDataInSlot(sprint, "tokens");
      const caInSlot = calculateSprintDataInSlot(sprint, "ca");
      return {
        ...sprint,
        tokens_total_in_slot: tokensInSlot.total,
        tokens_completed_in_slot: tokensInSlot.completed,
        ca_total_in_slot: caInSlot.total,
        ca_completed_in_slot: caInSlot.completed,
      };
    });

    const groupedByProject = sprintsWithSlotValues.reduce((acc, sprintInSlot) => {
      const projectId = sprintInSlot.project_id;
      if (!acc[projectId]) {
        acc[projectId] = {
          project_id: projectId,
          project_name: sprintInSlot.project_name || `Projet ${projectId}`,
          project_number: sprintInSlot.project_number,
          total_tokens_in_slot: 0,
          completed_tokens_in_slot: 0,
          total_ca_in_slot: 0,
          completed_ca_in_slot: 0,
          contributing_sprints_details: [] 
        };
      }
      acc[projectId].total_tokens_in_slot += sprintInSlot.tokens_total_in_slot;
      acc[projectId].completed_tokens_in_slot += sprintInSlot.tokens_completed_in_slot;
      acc[projectId].total_ca_in_slot += sprintInSlot.ca_total_in_slot;
      acc[projectId].completed_ca_in_slot += sprintInSlot.ca_completed_in_slot;
      
      acc[projectId].contributing_sprints_details.push({
        sprint_id: sprintInSlot.id, // Ajout de sprint_id pour la clé du map
        sprint_name: sprintInSlot.description || `Sprint ${sprintInSlot.id}`,
        tokens_total_in_slot: sprintInSlot.tokens_total_in_slot,
        tokens_completed_in_slot: sprintInSlot.tokens_completed_in_slot,
        ca_total_in_slot: sprintInSlot.ca_total_in_slot,
        ca_completed_in_slot: sprintInSlot.ca_completed_in_slot,
      });
      return acc;
    }, {});

    const aggregatedProjects = Object.values(groupedByProject).map(proj => {
      const currentModeTotal = mode === "tokens" ? proj.total_tokens_in_slot : proj.total_ca_in_slot;
      const currentModeCompleted = mode === "tokens" ? proj.completed_tokens_in_slot : proj.completed_ca_in_slot;
      return {
        ...proj,
        total: currentModeTotal,
        completed: currentModeCompleted,
        progress: currentModeTotal > 0 ? (currentModeCompleted / currentModeTotal) : 0
      };
    });

    return aggregatedProjects.sort((a, b) => b.total - a.total);
  }, [sprints, slot, mode]);

  const totalValue = useMemo(() => {
    return projectData.reduce((sum, project) => sum + project.total, 0);
  }, [projectData]);

  const baseColors = {
    0: { light: '#FF9B9B', dark: '#FF6B6B' }, 1: { light: '#FF9BFF', dark: '#FF6BFF' },
    2: { light: '#9BFFC4', dark: '#6BFF9F' }, 3: { light: '#9BCEFF', dark: '#6BA6FF' },
    4: { light: '#FFE59B', dark: '#FFD76B' }, 5: { light: '#E29BFF', dark: '#D26BFF' }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold mb-6 flex items-center">
          <Target className="h-5 w-5 mr-2" />
          {mode === "tokens"
            ? "Distribution des Tokens par Projet" // Titre modifié
            : "Distribution du CA par Projet"} {/* Titre modifié */}
        </h3>
        <button
          onClick={() => setMode(mode === "tokens" ? "ca" : "tokens")}
          className="px-2 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
        >
          {mode === "tokens" ? "Voir CA" : "Voir Tokens"}
        </button>
      </div>

      <div className="space-y-8">
        <div className="relative h-16">
          <div className="absolute inset-x-0 h-full flex rounded-lg overflow-visible">
            {projectData.map((project, index) => { // Itération sur projectData
              const widthPercent = totalValue > 0 ? (project.total / totalValue) * 100 : 0;
              const colors = baseColors[index % Object.keys(baseColors).length];
              return (
                <div
                  key={project.project_id} // Clé modifiée
                  className="relative h-full flex items-center justify-center group"
                  style={{ width: `${widthPercent}%`, backgroundColor: colors.light }}
                >
                  <div 
                    className="absolute h-full left-0 top-0"
                    style={{ width: `${project.progress * 100}%`, backgroundColor: colors.dark, transition: 'width 0.3s ease' }}
                  />
                  {widthPercent >= MIN_TEXT_WIDTH_PERCENT && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-800">
                      <div className="text-sm font-medium" style={{ overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, whiteSpace: 'normal', textAlign: 'center' }}>
                        {project.project_name} {/* Nom du projet */}
                      </div>
                      <div className="text-xs whitespace-nowrap">
                        {project.total} {mode === "tokens" ? "tokens" : "€"} ({Math.round(project.progress * 100)}%)
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out z-50 pointer-events-none w-max max-w-xs">
                    <div className="bg-gray-800 text-white text-xs py-2 px-3 rounded shadow-lg">
                      <div className="font-semibold text-sm mb-1">{project.project_name} (#{project.project_number})</div>
                      <div className="mb-1">
                        <span className="font-medium">Total Slot ({mode === "tokens" ? "Tokens" : "CA"}):</span> {project.completed} / {project.total}
                      </div>
                      <hr className="my-1 border-gray-600"/>
                      <div className="text-xs font-medium mb-0.5">Sprints contributeurs (valeurs dans ce slot):</div>
                      {project.contributing_sprints_details.length > 0 ? (
                          project.contributing_sprints_details.map(s => (
                              <div key={s.sprint_id} className="ml-1 text-gray-300 text-xs"> {/* text-xs pour sprint details */}
                                  - {s.sprint_name}:
                                  <br/>
                                  <span className="ml-2">T: {s.tokens_completed_in_slot}/{s.tokens_total_in_slot}</span>
                                  <span className="ml-2">CA: {s.ca_completed_in_slot}€/{s.ca_total_in_slot}€</span>
                              </div>
                          ))
                      ) : (
                          <span className="italic text-gray-400 text-xs">Aucun sprint contributeur.</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {projectData.map((project, index) => { // Itération sur projectData
            const colors = baseColors[index % Object.keys(baseColors).length];
            return (
              <div key={project.project_id} className="flex items-center space-x-2"> {/* Clé modifiée */}
                <div className="relative w-8 h-4 rounded overflow-hidden">
                  <div className="absolute inset-0" style={{ backgroundColor: colors.light }} />
                  <div 
                    className="absolute inset-y-0 left-0" 
                    style={{ backgroundColor: colors.dark, width: `${project.progress * 100}%` }} 
                  />
                </div>
                <div className="text-sm">
                  <div className="font-medium">{project.project_name}</div> {/* Nom du projet */}
                  <div className="text-gray-600">
                    {project.completed} / {project.total} {mode === "tokens" ? "tokens" : "€"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">Total Projets</p> {/* Titre modifié */}
            <p className="text-xl font-semibold">{projectData.length}</p> {/* Utilise projectData */}
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">
              Total {mode === "tokens" ? "Tokens" : "CA"}
            </p>
            <p className="text-xl font-semibold">{totalValue}</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">Progression Globale</p>
            <p className="text-xl font-semibold">
              {totalValue > 0 ? Math.round((projectData.reduce((sum, p) => sum + p.completed, 0) / totalValue) * 100) : 0}% {/* Utilise projectData */}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotTokensDistribution;
// --- END OF FILE SlotTokensDistribution.js ---